import { useRef, type ReactNode } from 'react';

interface KeepMountedProps {
  active: boolean;
  children: ReactNode;
}

/**
 * Renders `children` once `active` becomes true the first time, and keeps them
 * mounted forever afterwards (toggling visibility via `display: none` + `inert`).
 *
 * Used to preserve heavy subtrees — and the live DOM nodes inside them, like
 * `<img>` thumbnails — across in-app navigations so the browser doesn't have
 * to re-issue the underlying network requests when the user returns.
 */
export function KeepMounted({ active, children }: KeepMountedProps) {
  const hasMountedRef = useRef(false);
  if (active) hasMountedRef.current = true;
  if (!hasMountedRef.current) return null;

  return (
    // `inert` (when supported) skips the subtree for focus, pointer events,
    // and AT when hidden. React 18 types it as boolean.
    <div
      style={{ display: active ? 'contents' : 'none' }}
      aria-hidden={active ? undefined : true}
      inert={active ? undefined : true}
    >
      {children}
    </div>
  );
}
